import { Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
  credentialIssues,
  needsAttention,
  openActions,
  todayIso,
  weekState,
} from '../../../shared/projects.js'
import { Icon } from '../icons/Icon.jsx'
import { Menu } from '../ui/Menu.jsx'
import { UserMenu } from '../ui/UserMenu.jsx'
import p from '../projects/projects.module.css'
import styles from './Sidebar.module.css'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/**
 * The Projects app's left panel. Same layout as the Knowledge Base and Support
 * sidebars (brand, primary action, search, grouped list, user menu); the list
 * is the projects, grouped by status.
 */
export function ProjectsSidebar({ projects, activeId, view, canEdit, onNew, onRename, onSetStatus, onDelete }) {
  const [search, setSearch] = useState('')
  const q = search.trim().toLowerCase()
  const today = todayIso()

  const items = useMemo(() => {
    const all = projects.data ?? []
    return q ? all.filter((x) => `${x.name} ${x.client}`.toLowerCase().includes(q)) : all
  }, [projects.data, q])

  const all = projects.data ?? []
  const callsDue = all.filter((x) => ['due', 'missed'].includes(weekState(x, today))).length
  const issues = all.reduce((n, x) => n + credentialIssues(x, today).length, 0)
  const onAll = !activeId && !view

  return (
    <aside className={styles.sidebar}>
      <Link to="/" className={styles.brand} title="Back to DCKAP Hub">
        <span className={styles.mark}>
          <Icon name="logo" size={18} strokeWidth={1.7} />
        </span>
        <span className={styles.brandText}>
          <span className={styles.brandName}>Projects</span>
          <span className={styles.brandSub}>DCKAP Hub</span>
        </span>
      </Link>

      {canEdit ? (
        <button
          type="button"
          className={styles.newQuestion}
          onClick={onNew}
          style={{ font: 'inherit', fontSize: 14, fontWeight: 500, border: 0, width: '100%', cursor: 'pointer' }}
        >
          <span className={styles.newQuestionLabel}>
            <Icon name="plus" size={16} strokeWidth={1.7} />
            New project
          </span>
          <span className={styles.kbdDark}>{isMac ? '⌘ K' : 'Ctrl K'}</span>
        </button>
      ) : null}

      <label className={styles.search}>
        <Icon name="search" size={15} strokeWidth={1.6} />
        <span className="sr-only">Search projects</span>
        <input type="search" placeholder="Search projects" value={search} onChange={(e) => setSearch(e.target.value)} />
      </label>

      <div className={styles.group}>
        <Link to="/projects" className={`${styles.footerLink} ${onAll ? styles.footerLinkActive : ''}`} aria-current={onAll ? 'page' : undefined}>
          <Icon name="layers" size={16} strokeWidth={1.6} />
          All projects
          <span className={styles.footerMeta}>{all.length}</span>
        </Link>
        <Link to="/projects" search={{ view: 'weekly' }} className={`${styles.footerLink} ${view === 'weekly' ? styles.footerLinkActive : ''}`} aria-current={view === 'weekly' ? 'page' : undefined}>
          <Icon name="refresh" size={16} strokeWidth={1.6} />
          This week's calls
          {callsDue ? <span className={`${styles.footerMeta} ${p.sideCount}`}>{callsDue}</span> : null}
        </Link>
        <Link to="/projects" search={{ view: 'blocked' }} className={`${styles.footerLink} ${view === 'blocked' ? styles.footerLinkActive : ''}`} aria-current={view === 'blocked' ? 'page' : undefined}>
          <Icon name="warning" size={16} strokeWidth={1.6} />
          Blocked credentials
          {issues ? <span className={`${styles.footerMeta} ${p.sideCount} ${p.sideCountBad}`}>{issues}</span> : null}
        </Link>
      </div>

      <nav aria-label="Projects" className={styles.history}>
        {projects.isPending ? (
          <div className={styles.group} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.skeletonItem}>
                <div className="skeleton" style={{ height: 12, width: `${70 - i * 7}%` }} />
                <div className="skeleton" style={{ height: 10, width: '40%' }} />
              </div>
            ))}
          </div>
        ) : projects.isError ? (
          <p className={styles.empty}>
            Couldn't load your projects.{' '}
            <button type="button" className={styles.linkButton} onClick={() => projects.refetch()}>
              Retry
            </button>
          </p>
        ) : items.length === 0 ? (
          <p className={styles.empty}>{q ? 'No projects match.' : 'Your projects will show up here.'}</p>
        ) : (
          PROJECT_STATUSES.map((status) => {
            const group = items.filter((x) => x.status === status)
            if (!group.length) return null
            return (
              <div key={status} className={styles.group}>
                <div className={`section-label ${styles.groupLabel}`}>{PROJECT_STATUS_LABELS[status]}</div>
                {group.map((project) => {
                  const active = project.id === activeId
                  const open = openActions(project).length
                  return (
                    <div key={project.id} className={`${styles.item} ${active ? styles.itemActive : ''}`}>
                      <Link to="/projects" search={{ p: project.id }} className={styles.itemLink} aria-current={active ? 'page' : undefined}>
                        <span className={styles.itemTitle}>{project.name}</span>
                        <span className={p.sideClient}>{project.client}</span>
                      </Link>
                      <span className={p.sideBadges}>
                        {needsAttention(project, today) ? <span className={p.dot} title="Blocked credential or missed call" /> : null}
                        {open ? <span className={p.sideCount} title="Open actions">{open}</span> : null}
                      </span>
                      <Menu
                        trigger={(props) => (
                          <button type="button" className={`icon-btn ${styles.itemMenu}`} aria-label={`Options for ${project.name}`} {...props}>
                            <Icon name="dots" size={14} />
                          </button>
                        )}
                        items={
                          canEdit
                            ? [
                                { label: 'Rename', onSelect: () => onRename(project) },
                                ...PROJECT_STATUSES.filter((s) => s !== project.status).map((s) => ({
                                  label: `Move to ${PROJECT_STATUS_LABELS[s]}`,
                                  onSelect: () => onSetStatus(project, s),
                                })),
                                { label: 'Delete', danger: true, onSelect: () => onDelete(project) },
                              ]
                            : [{ label: 'View only', disabled: true, onSelect: () => {} }]
                        }
                      />
                    </div>
                  )
                })}
              </div>
            )
          })
        )}
      </nav>

      <div className={styles.footer}>
        <div className={styles.user}>
          <UserMenu />
        </div>
      </div>
    </aside>
  )
}
